/**
 * Promo price display.
 *
 * Finds server-rendered prices (snippets/price.liquid adds data-promo-context to .price-container)
 * and applies storefront promo strikethrough pricing from the shared promo-pricing utility.
 * Covers the PDP, sticky add to cart, featured product, quick buy and Liquid product cards,
 * including prices re-rendered after a variant change (watched with a MutationObserver).
 * Also fills promo disclaimer containers (blocks/campaign-disclaimer.liquid adds data-promo-disclaimers).
 *
 * All promo rules live in promo-pricing.js; this file only renders the result.
 */
import { getProductPromo, getProductDisclaimers } from '@theme/promo-pricing';
import { formatMoney } from '@theme/theme-currency';

const PRICE_SELECTOR = '.price-container[data-promo-context]';
const DISCLAIMER_SELECTOR = '[data-promo-disclaimers]';

function money(amount, format) {
  if (!format) return String(amount);
  return formatMoney(amount, { currencyCodeEnabled: 'true', moneyWithCurrencyFormat: format });
}

function readContext(container) {
  try {
    return JSON.parse(container.dataset.promoContext);
  } catch (error) {
    return null;
  }
}

function buildGroup(label, className, html) {
  const group = document.createElement('span');
  group.setAttribute('role', 'group');
  group.innerHTML = `<span class="visually-hidden">${label}&nbsp;</span><span class="${className}">${html}</span>`;
  return group;
}

function setSaveAmount(anchor, label, amountHtml, placeAfter) {
  const existing = placeAfter
    ? anchor.nextElementSibling?.classList.contains('save-amount-wrapper') && anchor.nextElementSibling
    : anchor.querySelector(':scope > .save-amount-wrapper');

  const saveEl = existing || document.createElement('span');
  saveEl.className = 'save-amount-wrapper';
  saveEl.innerHTML = `${label} ${amountHtml}`;

  if (!existing) {
    if (placeAfter) anchor.after(saveEl);
    else anchor.appendChild(saveEl);
  }
}

function applyPromo(container) {
  if (container.dataset.promoApplied === 'true') return;
  container.dataset.promoApplied = 'true';

  const context = readContext(container);
  if (!context) return;

  const promo = getProductPromo(context.product || {}, context.price);
  if (!promo) return;

  const priceEl = container.querySelector('.price');
  if (!priceEl) return;

  const regularLabel = container.dataset.labelRegular || '';
  const saleLabel = container.dataset.labelSale || '';
  const saveLabel = container.dataset.labelSave || '';
  const regularHtml = money(promo.original, context.format);
  const saleHtml = money(promo.discounted, context.format);
  const saveHtml = money(promo.saveAmount, context.format);

  // Make sure the sale price sits in its own labelled group, as the Liquid markup does for sales.
  let saleGroup = priceEl.parentElement;
  if (!saleGroup || saleGroup === container || saleGroup.getAttribute('role') !== 'group') {
    saleGroup = buildGroup(saleLabel, 'price', '');
    priceEl.replaceWith(saleGroup);
    saleGroup.querySelector('.price').replaceWith(priceEl);
  }

  let compareEl = container.querySelector('.compare-at-price');
  if (!compareEl) {
    const compareGroup = buildGroup(regularLabel, 'compare-at-price', '');
    if (context.saleFirst) saleGroup.after(compareGroup);
    else saleGroup.before(compareGroup);
    compareEl = compareGroup.querySelector('.compare-at-price');
  }

  // Promo applies to the current selling price, so that becomes the struck-through price.
  compareEl.innerHTML = regularHtml;
  priceEl.innerHTML = saleHtml;

  // "Save $X": after the snippet when it shows a saving itself, or inside the PDP price block.
  if (context.showSave) {
    setSaveAmount(container, saveLabel, saveHtml, true);
  }

  const priceBlock = container.closest('product-price[data-promo-save]');
  if (priceBlock) {
    setSaveAmount(priceBlock, saveLabel, saveHtml, false);
  }
}

function applyDisclaimers(container) {
  if (container.dataset.promoApplied === 'true') return;
  container.dataset.promoApplied = 'true';

  let context;
  try {
    context = JSON.parse(container.dataset.promoDisclaimers);
  } catch (error) {
    return;
  }

  const placement = String(context.placement || 'pdp').replace(/[^a-z0-9_-]/gi, '');
  const disclaimers = getProductDisclaimers(context.product || {}, {
    placement,
    offerType: context.offerType || 'product',
  });

  container.innerHTML = disclaimers
    .map(
      (html) =>
        `<div class="campaign-disclaimer campaign-disclaimer--${placement}" data-campaign-disclaimer data-placement="${placement}">${html}</div>`
    )
    .join('');
}

function applyAll(root = document) {
  if (root instanceof Element && root.matches(PRICE_SELECTOR)) applyPromo(root);
  root.querySelectorAll?.(PRICE_SELECTOR).forEach(applyPromo);

  if (root instanceof Element && root.matches(DISCLAIMER_SELECTOR)) applyDisclaimers(root);
  root.querySelectorAll?.(DISCLAIMER_SELECTOR).forEach(applyDisclaimers);
}

applyAll();

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) applyAll(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });